/**
 * The links the emails point at.
 *
 * `email-verification.html` links to `/verify-email?token=…` and
 * `password-reset.html` to `/reset-password?token=…`, both built by the *server*
 * from `APP_URL` — and until this existed **neither went anywhere**: there is no
 * router in this app, so both landed on the landing page and the token was
 * silently dropped. A verification email nobody can act on is worse than none,
 * and with the game gated behind verification it was a dead end.
 *
 * **Still not a router, and this is not the place to introduce one.** What is
 * needed is a single read of the URL at start-up and a way to forget it — three
 * lines of `history.replaceState`, not a history stack around a WebGL canvas.
 * `App.vue` already makes the same argument about the landing page.
 *
 * The parsing is a pure function of a path and a query string so it can be tested
 * without a document, which matters because getting it wrong is invisible: an app
 * that ignores a token looks exactly like an app with no deep links at all.
 */

export type DeepLinkKind = 'verify-email' | 'reset-password' | 'restart-confirm';

export interface DeepLink {
  kind: DeepLinkKind;
  token: string;
}

/**
 * Paths are matched with the trailing slash tolerated and the case ignored,
 * because a mail client that rewrites a link is not a case worth losing a
 * verification over. Anything else is not a deep link.
 */
const PATHS: Record<string, DeepLinkKind> = {
  '/verify-email': 'verify-email',
  '/reset-password': 'reset-password',
  // `restart_confirm` e-mail: /account/confirm?action=restart&token=… (a signed-in link, see `App.vue`).
  '/account/confirm': 'restart-confirm',
};

/**
 * The link in a URL, or null.
 *
 * **A path with no usable token is not a deep link.** Landing on
 * `/verify-email` with nothing after it would otherwise show a screen that can
 * only fail; the app opens normally instead, which is the better of the two. The
 * length bound matches the server's own DTO (`@Length(10, 256)`) so a truncated
 * link is refused here rather than after a round trip.
 */
export function parseDeepLink(pathname: string, search: string): DeepLink | null {
  const path = pathname.replace(/\/+$/, '').toLowerCase() || '/';
  const kind = PATHS[path];
  if (!kind) return null;
  // The same path would serve other confirmed actions; only the restart exists.
  if (kind === 'restart-confirm' && new URLSearchParams(search).get('action') !== 'restart') return null;

  const token = new URLSearchParams(search).get('token')?.trim() ?? '';
  if (token.length < 10 || token.length > 256) return null;

  return { kind, token };
}

/** Reads the live URL. The one impure line, kept out of the parser. */
export function readDeepLink(): DeepLink | null {
  if (typeof window === 'undefined') return null;
  return parseDeepLink(window.location.pathname, window.location.search);
}

/**
 * Puts the address bar back to the app's root.
 *
 * `replaceState` rather than `pushState`, so the browser's back button does not
 * walk back into a screen holding a token that has by then been spent — a
 * single-use token reused reads as "the link is broken" rather than as "you have
 * already used it". Called once the link has been acted on, not before: a reload
 * mid-flight should still be able to retry.
 */
export function clearDeepLink(): void {
  if (typeof window === 'undefined' || !window.history?.replaceState) return;
  window.history.replaceState(null, '', '/');
}
